/*
 * File: ApptFormDialog.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Dialog for creating/editing appointments
 */
import React, { useContext } from "react";
import { observer } from "mobx-react";
import CloseIcon from "@material-ui/icons/Close";
import UIDialog from "components/ui/UIDialog";
import ApptForm from "./ApptForm";
import UIStore from "../store/UIStore";
import { StoreContext, useStore } from "utils/useStore";
import { Instance } from "mobx-state-tree";
import ApptModel from "../store/ApptModel";
import ApptStore from "../store/DataStore";

interface Props {
  appt: Instance<typeof ApptModel>;
  title: string;
  store: Instance<typeof ApptStore>;
}

const ApptFormDialog = ({ appt, title, store }: Props) => {
  const storeManager = useContext(StoreContext);
  const uiStore = useStore(UIStore);

  if (!storeManager) {
    throw new Error("ApptFormDialog is missing StoreContext");
  }

  const onSubmit = () => {
    //Store is provided if form is opened from the Schedule page. If we're creating/updating an appointment from there, we want to reload on submit
    if (store) store.reloadAppts(storeManager);
    closeForm();
  };

  const closeForm = () => {
    uiStore.toggleFormDialog();
  };

  const onCancel = async () => {
    appt.cancel(storeManager.getStore(ApptStore));
    closeForm();
  };

  return (
    <UIDialog
      open={uiStore.showFormDialog}
      title={title}
      rightNav={() => <CloseIcon onClick={onCancel} color="inherit" />}
      content={() => <ApptForm parentCallBack={onSubmit} appt={appt} />}
      fullScreen
    />
  );
};

export default observer(ApptFormDialog);
